import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { ChevronRight, Palette, Shapes, ShoppingBag, Smartphone, Type, ToggleLeft, ToggleRight } from "lucide-react";
import { API_BASE_URL_V2 } from "../../api/URL_Header";

export const NAME_SEPARATOR = " || ";
export const VARIANT_META_PREFIX = "__catalogMeta:";
export const DEFAULT_LOW_STOCK = 5;

export function parseBilingual(value) {
  if (!value) return { ar: "", en: "" };
  const text = String(value);
  const index = text.indexOf(NAME_SEPARATOR);
  if (index === -1) return { ar: text, en: "" };
  return {
    ar: text.slice(0, index).trim(),
    en: text.slice(index + NAME_SEPARATOR.length).trim(),
  };
}

export function joinBilingual(ar, en) {
  const arabic = (ar || "").trim();
  const english = (en || "").trim();
  if (arabic && english) return `${arabic}${NAME_SEPARATOR}${english}`;
  return arabic || english;
}

export function displayAr(value) {
  return parseBilingual(value).ar || parseBilingual(value).en || "—";
}

export function displayEn(value) {
  return parseBilingual(value).en || "—";
}

export function resolveMediaUrl(path) {
  if (!path) return "";
  if (
    path.startsWith("http://") ||
    path.startsWith("https://") ||
    path.startsWith("data:") ||
    path.startsWith("blob:")
  ) {
    return path;
  }
  return `${API_BASE_URL_V2}${path.startsWith("/") ? "" : "/"}${path}`;
}

export function isCatalogMetaUrl(value) {
  return typeof value === "string" && value.startsWith(VARIANT_META_PREFIX);
}

export function splitVariantImages(imageUrls = []) {
  const urls = [];
  let meta = { nameAr: "", nameEn: "", lowStockThreshold: DEFAULT_LOW_STOCK };
  (imageUrls || []).forEach((item) => {
    if (isCatalogMetaUrl(item)) {
      try {
        meta = { ...meta, ...JSON.parse(item.slice(VARIANT_META_PREFIX.length)) };
      } catch {
        /* ignore malformed meta */
      }
    } else if (item) {
      urls.push(item);
    }
  });
  return { urls, meta };
}

export function joinVariantImages(urls = [], meta = {}) {
  const list = (urls || []).filter((item) => item && !isCatalogMetaUrl(item));
  list.push(
    `${VARIANT_META_PREFIX}${JSON.stringify({
      nameAr: meta.nameAr || "",
      nameEn: meta.nameEn || "",
      lowStockThreshold: Number(meta.lowStockThreshold) > 0 ? Number(meta.lowStockThreshold) : DEFAULT_LOW_STOCK,
      personalizationLabelAr: meta.personalizationLabelAr || "",
      personalizationLabelEn: meta.personalizationLabelEn || "",
    })}`
  );
  return list;
}

export function stockTone(quantity, threshold = DEFAULT_LOW_STOCK) {
  const qty = Number(quantity) || 0;
  const limit = Number(threshold) > 0 ? Number(threshold) : DEFAULT_LOW_STOCK;
  if (qty <= 0) return { label: "نفد المخزون", className: "bg-red-100 text-red-700", dot: "bg-red-500" };
  if (qty <= limit) return { label: "مخزون منخفض", className: "bg-orange-100 text-orange-700", dot: "bg-orange-500" };
  return { label: "متوفر", className: "bg-green-100 text-green-700", dot: "bg-green-500" };
}

export function compressImageFile(file, maxWidth = 900, quality = 0.85) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const scale = Math.min(1, maxWidth / img.width);
      canvas.width = Math.max(1, Math.round(img.width * scale));
      canvas.height = Math.max(1, Math.round(img.height * scale));
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(objectUrl);
      resolve(canvas.toDataURL("image/jpeg", quality));
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("تعذر قراءة الصورة"));
    };
    img.src = objectUrl;
  });
}

export function normalizeHex(value) {
  if (!value) return "";
  const trimmed = value.trim();
  if (/^#[0-9A-Fa-f]{6}$/.test(trimmed)) return trimmed.toUpperCase();
  if (/^[0-9A-Fa-f]{6}$/.test(trimmed)) return `#${trimmed.toUpperCase()}`;
  return trimmed;
}

export function CatalogNav() {
  const items = [
    { to: "/admin/catalog/category-types", label: "أنواع الفئات", icon: Shapes },
    { to: "/admin/catalog/colors", label: "الألوان", icon: Palette },
    { to: "/admin/catalog/fonts", label: "خطوط التخصيص", icon: Type },
    { to: "/admin/catalog/case-styles", label: "أنواع الكفر", icon: Smartphone },
    { to: "/admin/catalog/products", label: "المنتجات", icon: ShoppingBag },
  ];

  return (
    <div className="flex flex-wrap gap-2 mb-6">
      {items.map((item) => {
        const Icon = item.icon;
        return (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to !== "/admin/catalog/products"}
            className={({ isActive }) =>
              `flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border transition ${
                isActive
                  ? "bg-indigo-600 text-white border-indigo-600 shadow"
                  : "bg-white text-gray-700 border-gray-200 hover:border-indigo-300"
              }`
            }
          >
            <Icon size={16} />
            {item.label}
          </NavLink>
        );
      })}
    </div>
  );
}

export function CatalogShell({ title, subtitle, actions, children }) {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 p-4 md:p-8" dir="rtl">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <h1 className="text-2xl md:text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                {title}
              </h1>
            </div>
            {subtitle ? <p className="text-gray-500 text-sm md:text-base">{subtitle}</p> : null}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {actions}
            <button
              onClick={() => navigate("/dashboard")}
              className="flex items-center gap-2 bg-white/80 text-gray-700 px-5 py-3 rounded-xl shadow-md hover:shadow-xl border border-gray-200"
            >
              <ChevronRight size={20} />
              العودة للرئيسية
            </button>
          </div>
        </div>
        <CatalogNav />
        {children}
      </div>
    </div>
  );
}

export function ConfirmDialog({ open, title, message, confirmLabel = "حذف", onConfirm, onCancel, loading }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
        <h3 className="text-xl font-bold mb-2">{title}</h3>
        <p className="text-gray-600 mb-6">{message}</p>
        <div className="flex gap-3 justify-end">
          <button
            onClick={onCancel}
            className="px-4 py-2 rounded-xl bg-gray-100 text-gray-700"
            disabled={loading}
          >
            إلغاء
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 rounded-xl bg-red-600 text-white font-semibold"
            disabled={loading}
          >
            {loading ? "جاري التنفيذ..." : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export function StockBadge({ quantity, threshold }) {
  const tone = stockTone(quantity, threshold);
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${tone.className}`}>
      <span className={`w-2 h-2 rounded-full ${tone.dot}`} />
      {tone.label} ({Number(quantity) || 0})
    </span>
  );
}

export function TableSkeleton({ rows = 5 }) {
  return (
    <div className="animate-pulse p-4 space-y-4">
      {[...Array(rows)].map((_, index) => (
        <div key={index} className="flex gap-4">
          <div className="w-12 h-12 bg-gray-200 rounded-full" />
          <div className="flex-1 space-y-2">
            <div className="h-4 bg-gray-200 rounded w-2/3" />
            <div className="h-4 bg-gray-200 rounded w-1/3" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function EmptyState({ text }) {
  return <div className="p-10 text-center text-gray-500">{text}</div>;
}

export function Switch({ checked, onChange, label }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className="flex items-center gap-2 text-sm font-medium"
    >
      {checked ? (
        <ToggleRight size={28} className="text-indigo-600" />
      ) : (
        <ToggleLeft size={28} className="text-gray-400" />
      )}
      {label}
    </button>
  );
}
